import type { CSSProperties, ReactNode } from 'react'
import { CORAL, INK, MARIGOLD, NIGHT, RADIUS_CARD } from '../tokens'

type BandTone = 'marigold' | 'coral' | 'night'

interface TopTearCardProps {
  icon: ReactNode
  title: string
  subtitle?: string
  tone?: BandTone
  children?: ReactNode
  /** Positioned absolutely at the tear line, same technique as TornCard's
   *  `spill` -- pass `SpillingDoodlesCompact` for the scaled-down version
   *  sized for this shorter band. */
  spill?: ReactNode
  style?: CSSProperties
}

const bandFills: Record<BandTone, { bg: string; fg: string; sub: string }> = {
  marigold: { bg: MARIGOLD, fg: INK, sub: '#4A4640' },
  coral: { bg: CORAL, fg: '#fff', sub: 'rgba(255,255,255,.85)' },
  night: { bg: NIGHT, fg: '#fff', sub: '#B9BFD9' },
}

// Same rhythm as TornCard's hero tear, compressed for a short band -- see
// /redesignplan.md section 9/10: the tear only applies to cards that are
// full-width at their current breakpoint (this component), never to grid
// tiles sitting side by side.
const BAND_CLIP_PATH =
  'polygon(0% 0%, 100% 0%, 100% 62%, 95% 78%, 89% 65%, 83% 82%, 77% 68%, 71% 84%, 65% 66%, 59% 80%, 53% 64%, 47% 82%, 41% 68%, 35% 84%, 29% 66%, 23% 80%, 17% 64%, 11% 82%, 5% 68%, 0% 84%)'

/**
 * A full-width card that tears from the top -- a compressed version of
 * TornCard's hero device, for content sections rather than the page hero
 * (Worth a quick review, a check-in prompt, an assignments list). Only use
 * this for cards that are genuinely full-width; grid tiles sitting next to
 * other tiles should use the plain `Card` instead.
 */
export function TopTearCard({ icon, title, subtitle, tone = 'coral', children, spill, style }: TopTearCardProps) {
  const { bg, fg, sub } = bandFills[tone]
  return (
    <div
      style={{
        borderRadius: RADIUS_CARD,
        border: `2px solid ${INK}`,
        boxShadow: `0 4px 0 ${INK}`,
        overflow: 'hidden',
        background: '#fff',
        ...style,
      }}
    >
      <div
        style={{
          background: bg,
          // Bottom padding must clear the deepest point of the zigzag (84% down)
          // or the tear cuts through the subtitle line instead of below it.
          padding: subtitle ? '1rem 1.4rem 2.1rem' : '1rem 1.4rem 1.3rem',
          clipPath: BAND_CLIP_PATH,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{ color: fg, display: 'flex' }}>{icon}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: fg }}>{title}</div>
            {subtitle && <div style={{ fontSize: '0.78rem', color: sub }}>{subtitle}</div>}
          </div>
        </div>
      </div>
      {children && (
        <div style={{ padding: '1.6rem 1.4rem 1.3rem', position: 'relative' }}>
          {spill && (
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: '100%', height: 0, zIndex: 2 }}>
              {spill}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}
